/**
 * GrupoPadrao.service.js
 *
 * Único caminho do client para garantir o grupo "Associados" no próprio cadastro.
 *
 * Desde 04/09/2026 a regra de `users` exige 100/102 (Grupos de usuários) para
 * mexer em `usersgroupsowned` — o campo de onde o `AuthContext` deriva TODAS as
 * permissões da interface e de onde o espelho `rulesowned` é gerado. Sem essa
 * trava, qualquer autenticado se colocava em "Administradores de sistema" no
 * próprio documento.
 *
 * O usuário comum não tem 100/102, então a concessão do grupo padrão passou para
 * a Cloud Function `garantirGrupoPadrao` (Admin SDK). Ela só mexe no documento de
 * quem chamou, só concede "Associados" e só acrescenta — ver
 * `functions/grupoPadraoUsuario.js`.
 *
 * @module services/GrupoPadrao.service
 */

import { httpsCallable } from 'firebase/functions';
import { functionsSAE1 } from '../utils/init-firebase';

/**
 * Garante o vínculo com o grupo padrão no portal da CBTKD e, se informado, no
 * portal de origem.
 *
 * Idempotente: chamar de novo com o mesmo portal não altera nada.
 *
 * @param {string} [portalID] portal de origem (o da CBTKD entra sempre)
 * @param {string} [uid] alvo. Omitido = o próprio usuário. Informar o de OUTRA
 *   pessoa exige 200 (Usuários : Criar) ou 300 (Pessoas : Criar) — é o caso do
 *   operador que acabou de cadastrar um praticante. A function valida.
 * @returns {Promise<{ok: boolean, alterado: boolean, usersgroupsowned: Array}>}
 * @throws repassa o erro da callable — quem chama decide se bloqueia o fluxo
 */
export async function garantirGrupoPadrao(portalID, uid) {
  const fn = httpsCallable(functionsSAE1, 'garantirGrupoPadrao');
  const { data } = await fn({ portalID: portalID || '', ...(uid ? { uid } : {}) });
  return data;
}

/**
 * Concede ou remove os grupos de um VÍNCULO (cargo em departamento ou área de
 * atuação) no cadastro de outra pessoa.
 *
 * O client não escolhe os grupos: aponta a ORIGEM e a function lê de lá
 * (`departamentos/{id}.cargos[].rulesOwned` ou `areasatuacao/{id}.rulesOwned`).
 * É isso que impede pedir "Administradores de sistema" por este caminho.
 * Exige 300/302 (Pessoas : Criar / Atualizar) — validado no servidor.
 *
 * @param {object} p
 * @param {string} p.uid       cadastro que recebe (ou perde) os grupos
 * @param {string} p.portalID  portal do vínculo
 * @param {'cargo'|'areaAtuacao'} p.origem
 * @param {string} p.origemId  id do departamento ou da área de atuação
 * @param {string} [p.cargoId] uid do cargo dentro do departamento (origem 'cargo')
 * @param {boolean} [p.remover] true remove em vez de conceder
 * @param {string} [p.vinculoId] docId em `praticantes.dadosColaborador.administrativo`
 *   do vínculo que está saindo. Obrigatório (ou `idEmpregador`) ao remover um
 *   vínculo de cargo — ver abaixo.
 * @param {string} [p.idEmpregador] entidade do vínculo que está saindo, quando
 *   não se tem o docId.
 *
 * ── Por que a remoção precisa dizer QUAL vínculo está saindo (P18) ──────────
 * Uma pessoa pode ser colaboradora em duas entidades do mesmo portal, e os dois
 * vínculos concedem o mesmo grupo. A function varre os vínculos que SOBRAM e
 * subtrai da remoção o que eles ainda sustentam — para isso ela precisa saber o
 * que está saindo. Sem `vinculoId` nem `idEmpregador` ela recusa a chamada, em
 * vez de remover incondicionalmente.
 *
 * `portalID` pode ser o id da ENTIDADE ou o do portal: a function resolve (é
 * idempotente). Antes disso o client resolvia — e resolvia diferente em cada
 * chamador.
 */
export async function gruposDeVinculo({
  uid, portalID, origem, origemId, cargoId, remover, vinculoId, idEmpregador,
}) {
  const fn = httpsCallable(functionsSAE1, 'gruposDeVinculo');
  const { data } = await fn({
    uid, portalID, origem, origemId, cargoId,
    remover: remover === true,
    ...(vinculoId ? { vinculoId } : {}),
    ...(idEmpregador ? { idEmpregador } : {}),
  });
  return data;
}

/**
 * Cria o cadastro (conta no Firebase Auth + documento `users` + praticante
 * mínimo + grupo padrão) NO SERVIDOR, numa transação só — P13.
 *
 * Por que saiu do cliente: até 09/09/2026 o `create` de `users` era aberto a
 * qualquer um dos ~42 mil autenticados, e era o cliente que decidia o conteúdo
 * do documento. Fechar isso só pela regra (`próprio || temRule([200,300])`) foi
 * MEDIDO e rejeitado: bloquearia 378 autores e 4.442 cadastros em 365 dias —
 * professores e academias cadastrando os próprios alunos por rotas abertas a
 * qualquer autenticado POR DESENHO. E quebraria sujo, deixando uma conta órfã
 * no Auth por tentativa, porque a conta é criada ANTES da gravação.
 *
 * O que a callable garante e o cliente não garantia:
 *   · `metadados` é do servidor, inclusive `successorId: ""` — campo ausente
 *     nunca casa com `== ""` e some das listagens (foram 271 cadastros assim);
 *   · nada de `rulesowned`/`isSuperAdmin`, e grupo só com 100/102;
 *   · falhou a gravação, a conta criada na chamada é DESFEITA;
 *   · idempotência por IDENTIDADE, no servidor — dois dispositivos ao mesmo
 *     tempo convergem para o mesmo uid. O coalescedor do cliente
 *     (utils/coalescerCadastro.js) só cobria a aba.
 *
 * @param {object} p
 * @param {object} p.users      documento `users` a criar (o servidor sanitiza)
 * @param {string} [p.uid]      uid desejado; vazio = o Auth gera
 * @param {string} [p.portalID] portal de origem, para o grupo padrão
 * @param {boolean} [p.criarPraticante] false pula o praticante mínimo
 * @returns {Promise<{ok: boolean, uid: string, criado: boolean, contaCriada: boolean}>}
 */
export async function criarCadastroUsuario({ users, uid, portalID, criarPraticante }) {
  const fn = httpsCallable(functionsSAE1, 'criarCadastroUsuario');
  const { data } = await fn({
    users,
    ...(uid ? { uid } : {}),
    ...(portalID ? { portalID } : {}),
    ...(criarPraticante === false ? { criarPraticante: false } : {}),
  });
  return data;
}

export default { garantirGrupoPadrao, gruposDeVinculo, criarCadastroUsuario };
